import React, {
  Dispatch, Ref, SetStateAction, useRef,
} from 'react';
import {
  StyleSheet, View, Text, ScrollView, Pressable,
} from 'react-native';
import {
  AssetLabels, AssetTypeTitles, MeterLabel, SensorLabel, TemplateType, TemplateTypeTitles,
} from '../../../../constants/Types';
import Colors from '../../../../constants/Colors';
import PressableStyled, { PressableState } from '../../../PressableStyled';
import Style from '../../../../constants/Style';
import DownArrow24 from '../../../24pxIcons/DownArrow24';
import TickIcon12 from '../../../12pxIcons/TickIcon12';
import CloseIconSmall from '../../../12pxIcons/CloseIconSmall';

interface AssetLabelSelectorProps {
  templateType: TemplateType,
  labels: (SensorLabel | MeterLabel)[],
  setLabels: Dispatch<SetStateAction<(SensorLabel | MeterLabel)[]>>,
  invalid: boolean,
  labelsDropdownShown: boolean,
  setLabelsDropdownShown: Dispatch<SetStateAction<boolean>>,
}

const AssetLabelSelector = ({
  templateType = TemplateType.ASSET, labels, setLabels, invalid, labelsDropdownShown,
  setLabelsDropdownShown,
}: AssetLabelSelectorProps) => {
  if (templateType === TemplateType.ASSET) return null;

  const scrollRef: Ref<ScrollView> = useRef<ScrollView | null>(null);

  const removeLabel = (item: SensorLabel | MeterLabel) => setLabels(labels
    ?.filter((label) => label !== item));

  return <View style={styles.container}>
    <Text style={styles.fieldTitle}>{`${TemplateTypeTitles[templateType]} labels *`}</Text>
    <View style={[styles.border, styles.button, invalid && styles.invalid, { flex: 1 }]}>
      {labels?.length > 0 && <ScrollView
        horizontal
        style={styles.labelSection}
        ref={scrollRef}>
        <View style={Style.row}>
          {labels?.map((label) => <View
            style={styles.label}
            key={`label_${label}`}
          >
            <Text style={styles.labelText}>{label}</Text>
            <PressableStyled onPress={() => removeLabel(label)}>
              <CloseIconSmall />
            </PressableStyled>
          </View>)}
        </View>
      </ScrollView>}
      <PressableStyled
        style={[Style.row, !(labels?.length > 0) && { flex: 1 }]}
        onPress={() => setLabelsDropdownShown(!labelsDropdownShown)}
      >
        {labels.length ? null : <Text style={styles.value}>{'Choose option'}</Text>}
        <View>
          <DownArrow24
            style={labelsDropdownShown ? Style.inverted : Style.upright}
            color={invalid ? styles.invalid.borderColor : styles.checkBoxSelected.borderColor}
          />
        </View>
      </PressableStyled>
    </View>
    <View>
      {labelsDropdownShown && <ScrollView style={[styles.border, styles.dropdown]}>
        {AssetLabels[templateType].map((item: SensorLabel | MeterLabel) => {
          const selected: boolean = labels.includes(item);

          return <Pressable
            key={`label_option_${item}`}
            style={styles.item}
            onPress={() => {
              if (selected) removeLabel(item);
              else {
                setLabels([...labels, item]);
                scrollRef?.current?.scrollToEnd();
              }
            }}
          >
            {({ hovered, pressed }: PressableState) => <>
              <View style={[
                styles.checkBox, hovered && Style.hovered, pressed && Style.pressed,
                selected && styles.checkBoxSelected,
              ]}>
                {selected && <TickIcon12 color={styles.checkBoxSelected.borderColor} />}
              </View>
              <Text style={styles.value}>
                {`${AssetTypeTitles[item]} (${item})`}
              </Text>
            </>}
          </Pressable>;
        })}
      </ScrollView>}
    </View>
    {invalid && <Text style={styles.invalidText}>{'At least one label must be selected'}</Text>}
  </View>;
};

const styles = StyleSheet.create({
  container: {
    zIndex: 3,
    marginTop: 16,
  },
  fieldTitle: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 4,
  },
  border: {
    borderRadius: 10,
    borderWidth: 1.5,
    backgroundColor: '#FFF',
    borderColor: Colors.light.hereworksBlue100,
  },
  button: {
    ...Style.row,
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderRadius: 10,
    borderWidth: 1.5,
    backgroundColor: '#FFF',
    borderColor: Colors.light.hereworksBlue100,
    minHeight: 48,
  },
  item: {
    ...Style.row,
    paddingHorizontal: 12,
    paddingVertical: 9,
    alignSelf: 'stretch',
  },
  value: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    flex: 1,
  },
  dropdown: {
    position: 'absolute',
    top: 4,
    maxHeight: 180,
    width: '100%',
    borderRadius: 10,
    borderWidth: 1.5,
    backgroundColor: '#FFF',
    borderColor: Colors.light.hereworksBlue100,
  },
  checkBox: {
    borderRadius: 2,
    borderWidth: 1,
    width: 16,
    height: 16,
    borderColor: Colors.light.hereworksBlue100,
    marginEnd: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkBoxSelected: {
    borderColor: Colors.light.hereworksBlue500,
  },
  labelSection: {
    flex: 1,
    flexDirection: 'row',
    marginEnd: 8,
  },
  label: {
    ...Style.row,
    height: 24,
    borderRadius: 4,
    backgroundColor: Colors.light.hereworksDarkBlue,
    paddingHorizontal: 8,
    marginEnd: 4,
    marginVertical: 2,
  },
  labelText: {
    fontSize: 14,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: '#FFF',
    marginStart: 4,
    marginEnd: 8,
  },
  invalid: {
    borderColor: Colors.light.hereworksRed500,
  },
  invalidText: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksRed500,
    marginTop: 4,
    zIndex: -1,
  },
});

export default AssetLabelSelector;
